
export default function OurInheritedPropertyProcess() {
  const steps = [
    {
      number: '1',
      title: 'Initial Consultation',
      duration: '30 minutes',
      description:
        'Share your situation—probate status, property condition, heir dynamics, and timeline needs. We listen and provide honest guidance.',
      whatWeNeed: 'Basic property info, probate status, contact for all heirs (if applicable)'
    },
    {
      number: '2',
      title: 'Property Evaluation',
      duration: '2-3 days',
      description:
        'We assess the property remotely or in-person, review financials, and analyze comparable sales. No pressure, no obligations.',
      whatWeNeed: 'Property access, rent roll, recent expenses (if available)'
    },
    {
      number: '3',
      title: 'Cash Offer Presentation',
      duration: '1-2 days',
      description:
        'Receive a fair, all-cash offer with clear explanation of our valuation. We show our work and answer all questions.',
      whatWeNeed: 'Your time to review and discuss with co-heirs'
    },
    {
      number: '4',
      title: 'Probate Coordination',
      duration: 'Varies',
      // Use double quotes to avoid breaking the string with an apostrophe
      description:
        "We work with your probate attorney to prepare court petitions, obtain appraisals, and secure sale approval. We've done this hundreds of times.",
      whatWeNeed: 'Probate attorney contact, court information'
    },
    {
      number: '5',
      title: 'Fast Closing',
      duration: '14-30 days',
      description:
        'Once court approves, we close quickly with cash. No financing contingencies, no repair requests, no last-minute surprises.',
      whatWeNeed: 'Signed court order, clear title'
    }
  ];

  return (
    <section id="process" className="py-20 bg-gradient-to-br from-teal-50 to-cyan-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Our Specialized Process for Inherited Properties
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We've refined this process through hundreds of inherited property transactions. It's designed to
            minimize stress, maximize speed, and ensure fair outcomes for all heirs.
          </p>
        </div>

        <div className="space-y-8 mb-16">
          {steps.map((step, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-600 text-white rounded-full text-3xl font-bold shadow-lg">
                    {step.number}
                  </div>
                </div>
                <div className="flex-grow">
                  <div className="flex flex-wrap items-center justify-between mb-4">
                    <h3 className="text-3xl font-bold text-gray-900">{step.title}</h3>
                    <span className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold">
                      {step.duration}
                    </span>
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">{step.description}</p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">What We Need From You:</p>
                    <p className="text-gray-700">{step.whatWeNeed}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white rounded-xl p-8 text-center shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-full mx-auto mb-4">
              <i className="ri-hand-coin-line text-3xl"></i>
            </div>
            <h4 className="text-2xl font-bold mb-3 text-gray-900">All-Cash Offers</h4>
            <p className="text-gray-700 leading-relaxed">
              No financing contingencies means faster closing and certainty for all heirs.
            </p>
          </div>
          <div className="bg-white rounded-xl p-8 text-center shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-full mx-auto mb-4">
              <i className="ri-tools-line text-3xl"></i>
            </div>
            <h4 className="text-2xl font-bold mb-3 text-gray-900">Buy AS IS</h4>
            <p className="text-gray-700 leading-relaxed">
              No repairs, no updates, no cleaning required. We handle everything.
            </p>
          </div>
          <div className="bg-white rounded-xl p-8 text-center shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-full mx-auto mb-4">
              <i className="ri-calendar-check-line text-3xl"></i>
            </div>
            <h4 className="text-2xl font-bold mb-3 text-gray-900">Flexible Timing</h4>
            <p className="text-gray-700 leading-relaxed">
              We work around probate schedules and family needs—no pressure.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-10 text-white">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-500 rounded-full flex-shrink-0">
              <i className="ri-shield-check-line text-3xl"></i>
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-4">Why Families Trust Us</h3>
              <p className="text-xl text-white/90 mb-6 leading-relaxed">
                We understand that selling an inherited property isn't just a transaction—it's an emotional process
                during a difficult time. Our team treats every family with respect, transparency, and patience.
              </p>
              <ul className="space-y-3 text-lg">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-white/90">We've closed over 500 inherited property transactions</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-white/90">Our offers are fair and backed by transparent market analysis</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-white/90">We coordinate with attorneys, courts, and all heirs seamlessly</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-white/90">No hidden fees, no surprises, no pressure tactics</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
